/**
 * Typed wrapper for the citizen-side bill payment endpoints on
 * service-financial-services (port 3110).
 *
 * Endpoints:
 *   - GET  /v1/bills/providers      — list active billers
 *   - POST /v1/bills/lookup         — look up customer/account on biller
 *   - POST /v1/bills/pay            — debit wallet + persist bill_payment
 *   - GET  /v1/bills/payments/me    — list citizen's own bill payment receipts
 *
 * RBAC: GET providers + POST lookup → Bills/read-providers;
 *       POST pay + GET payments/me → Bills/pay-own.
 *
 * Mirrors `apps/service-financial-services/src/modules/wallet/citizen-bills.controller.ts`.
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface CitizenBillProvider {
  readonly id: string;
  readonly code: string;
  readonly category: string;
  readonly displayName: string;
  readonly inputSchema: unknown;
}

export interface ListCitizenBillProvidersResponse {
  readonly items: readonly CitizenBillProvider[];
}

export interface BillLookupRequest {
  readonly providerCode: string;
  readonly accountNumber: string;
}

export interface BillLookupResponse {
  readonly providerCode: string;
  readonly accountNumber: string;
  readonly customerName: string;
  readonly outstandingAmount: string;
  readonly currency: string;
}

export interface PayBillRequest {
  readonly providerCode: string;
  readonly accountNumber: string;
  readonly amount: string;
  readonly currency?: string;
  readonly idempotencyKey?: string;
}

export interface BillPaymentReceipt {
  readonly id: string;
  readonly providerCode: string;
  readonly providerRef: string;
  readonly walletTransactionId: string;
  readonly amount: string;
  readonly currency: string;
  readonly walletBalance: string;
  readonly status: string;
  readonly receipt: Record<string, unknown>;
}

export interface MyBillPaymentRow {
  readonly id: string;
  readonly providerCode: string;
  readonly providerRef: string | null;
  readonly amount: string;
  readonly status: string;
  readonly paidAt: string | null;
}

export interface ListMyBillPaymentsResponse {
  readonly items: readonly MyBillPaymentRow[];
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function listCitizenBillProviders(
  options: CallOpts = {},
): Promise<ListCitizenBillProvidersResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListCitizenBillProvidersResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/bills/providers',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function lookupBill(
  body: BillLookupRequest,
  options: CallOpts = {},
): Promise<BillLookupResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<BillLookupResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/bills/lookup',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function payCitizenBill(
  body: PayBillRequest,
  options: CallOpts = {},
): Promise<BillPaymentReceipt> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<BillPaymentReceipt>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/bills/pay',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listMyBillPayments(
  options: CallOpts = {},
): Promise<ListMyBillPaymentsResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListMyBillPaymentsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/bills/payments/me',
    bearerToken: bearerOf(options),
    ...rest,
  });
}
